import React, { useRef, useState } from 'react';
import * as yup from 'yup';
import { observer } from 'mobx-react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Alert, Box, Button, Typography } from '@mui/material';
import {
  SubSection,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import { IconLibrary, useServiceJob } from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import DispatchCreateServiceRequests from '../components/OfferBased/DispatchCreateServiceRequests';
import DispatchCreateServiceRequestValidation from '../components/OfferBased/DispatchCreateServiceRequestValidation';

const FORM_ID = 'DISPATCH_ALTERNATE_TIME_FORM';

const schema = [
  {
    control: 'custom',
    customComponent: 'DispatchCreateServiceRequests',
    name: 'customerServiceRequests',
    label: {
      localeId: 'CUSTOMER_SERVICE_REQUESTED_DATES',
      defaultMessage: 'Customer Service Requested Date(s)',
    },
    validationMessage: {
      id: 'DATE_IS_REQUIRED',
      defaultMessage: 'Date is required',
    },
    column: 1,
    row: 1,
    required: false,
    max: 3,
  },
];

function validationSchema(formatMessageFn) {
  return yup.object().shape({
    customerServiceRequests: yup
      .array()
      .of(
        DispatchCreateServiceRequestValidation(
          schema[0].validationMessage,
          formatMessageFn,
        ),
      )
      .nullable()
      .test('inject-row-count', '', function injectRowCount(rows) {
        this.options.context = {
          ...this.options.context,
          rowCount: rows?.length ?? 0,
        };
        return true;
      }),
  });
}

function OfferBased({ isRescheduleFlow = false }) {
  const navigate = useNavigate();
  const { serviceJob } = useServiceJob();
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const [success, setSuccess] = useState(false);
  const [open, setOpen] = useState(false);

  const jobId = serviceJob?.id;
  const jobNumber = serviceJob?.serviceJobNumber;
  const recordVersion = serviceJob?.recordVersion;
  const { customerServiceRequests = [] } = serviceJob;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, put, getApiUrl } = useDataProvider(provider);

  const dispatchJobWithAlternateTime = async ({ data, isReschedule }) => {
    let url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/dispatch/offers`;
    let method = post;

    if (isReschedule) {
      url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/reschedule`;
      method = put;
    }
    const response = await method(url, {
      ...data,
      recordVersion,
    });
    return response;
  };

  const returnToJobPage = () => {
    navigate(`/servicejobs/edit/${serviceJob?.id}`);
  };

  const onDialogClose = () => {
    setOpen(false);
    returnToJobPage(null);
  };

  const buttons = [
    {
      label: isRescheduleFlow
        ? formatMessage({
            id: 'RESCHEDULE',
            defaultMessage: 'Reschedule',
          })
        : formatMessage({
            id: 'DISPATCH',
            defaultMessage: 'Dispatch',
          }),
      onClick: () => formRef.current.submit({ formId: FORM_ID }),
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Dispatch />,
    },
    {
      label: formatMessage({
        id: 'RESET',
        defaultMessage: 'Reset',
      }),
      onClick: () => formRef.current.reset(),
      variant: 'outlined',
      color: 'primary',
      startIcon: <IconLibrary.Reset />,
    },
  ];

  const preProcessSubmitData = data => {
    return data;
  };

  const onSubmit = async data => {
    const newData = preProcessSubmitData(data);
    const response = await dispatchJobWithAlternateTime({
      data: newData,
      isReschedule: isRescheduleFlow,
    });
    if (response?.ok) {
      return {
        nextValues: data,
        errors: null,
        callback: () => {
          setSuccess(true);
          setOpen(true);
        },
      };
    }

    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  return (
    <Box>
      {!success ? (
        <Box sx={{ pt: 1 }}>
          <Alert sx={{ mb: 2, py: 0.5, px: 1.2 }} severity="info">
            <FormattedMessage
              id="OFFER_BASED_CSR_INFO"
              defaultMessage="Enter Customer Preferred Service Dates to communicate scheduling preferences, or leave blank to allow scheduling based on Service Provider availability."
            />
          </Alert>
          <SubSection>
            <Form
              dialog
              submitWithReadOnlyFields
              submitWithoutUpdates
              formId={FORM_ID}
              ref={formRef}
              onSubmit={onSubmit}
              schema={schema}
              validationSchema={validationSchema(formatMessage)}
              permittedByDefault
              customComponents={{
                DispatchCreateServiceRequests,
              }}
              defaultValues={{
                customerServiceRequests: customerServiceRequests || null,
              }}>
              <Box
                display="flex"
                sx={{
                  justifyContent: 'center',
                  gap: 2,
                  mt: 3,
                }}>
                {buttons.map(btn => {
                  const { label, ...rest } = btn;
                  return (
                    <Button key={label} {...rest}>
                      {label}
                    </Button>
                  );
                })}
              </Box>
            </Form>
          </SubSection>
        </Box>
      ) : (
        <Dialog
          open={open}
          onClose={() => {
            onDialogClose(null);
          }}
          maxWidth="xs"
          fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isRescheduleFlow ? (
              <IconLibrary.RescheduleServiceJob />
            ) : (
              <IconLibrary.DispatchAlternateTime />
            )}
            {isRescheduleFlow ? (
              <FormattedMessage
                id="JOB_RESCHEDULED"
                defaultMessage="Job Rescheduled"
              />
            ) : (
              <FormattedMessage
                id="JOB_DISPATCHED"
                defaultMessage="Job Dispatched"
              />
            )}
            <DialogCloseButton
              onClose={() => {
                onDialogClose(null);
              }}
            />
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }} textAlign="center">
              <Typography>
                {isRescheduleFlow ? (
                  <FormattedMessage
                    id="JOB_SERVICEJOBNUMBER_WAS_RESCHEDULED_SUCCESSFULLY"
                    defaultMessage="Job {servicejobnumber} was rescheduled successfully."
                    values={{ servicejobnumber: jobNumber }}
                  />
                ) : (
                  <FormattedMessage
                    id="JOB_SERVICEJOBNUMBER_WAS_DISPATCHED_SUCCESSFULLY"
                    defaultMessage="Job {servicejobnumber} was dispatched successfully."
                    values={{ servicejobnumber: jobNumber }}
                  />
                )}
              </Typography>
              <Box
                display="flex"
                sx={{
                  justifyContent: 'center',
                  mt: 3,
                }}>
                <Button
                  onClick={() => returnToJobPage()}
                  variant="outlined"
                  startIcon={<IconLibrary.Close />}>
                  <FormattedMessage id="CLOSE" defaultMessage="Close" />
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default observer(OfferBased);
